/** Pestaña + popup de bienvenida y WhatsApp flotante. Sin JS, el dialog no se abre y la pestaña no hace nada visible.
 * El {% form 'customer' %} del popup viaja en <template data-promo-template> y se clona al abrir (ADR 0016): mientras no hay
 * formulario de cliente en el documento, Shopify no carga hCaptcha. Si la página vuelve de un envío (código o errores), se
 * clona y se abre de entrada.
 * Los flotantes esquivan las franjas con data-float-avoid (pie de precio del hero, bloque de compra y armado de la ficha, botón
 * «Verla en tu espacio» del visor 3D y botón de checkout de /cart, T-052):
 * mientras una está en pantalla, un IntersectionObserver activa un scroll pasivo (con rAF) que sube --floats-bottom (inline
 * en <html>, así lo lee también el badge de hCaptcha de Shopify) justo por encima de ella (clase floats--lift) y lo suelta
 * cuando sale. Suben WhatsApp y el badge; la pestaña «10% off» nunca sube (queda como pestaña abajo y el CSS la oculta
 * hasta 1279 px mientras la franja pasa). Con data-float-avoid="compact" (bloque de compra y armado de la ficha) además se
 * marca floats--compact (desde T-051 WhatsApp es siempre solo el círculo; la clase queda por si vuelve una pastilla). */
const FLOAT_SIZE = 56; // círculo de WhatsApp
const FLOAT_GAP = 12; // aire entre el círculo y la franja que esquiva
const FLOAT_BASE = 20; // --floats-bottom por defecto
const TAB_ZONE = 64; // alto de la pestaña «10% off» pegada abajo (con aire)
class PromoFloat extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.avoid(signal);
    this.dialog = this.querySelector('dialog');
    this.tab = this.querySelector('[data-promo-open]');
    if (!this.dialog || !this.tab) return;
    this.tab.addEventListener('click', () => this.open(), { signal });
    this.revealTab(signal);
    this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-promo-close]')) return this.close();
      if (!event.target.closest('[data-promo-card]')) this.close();
    }, { signal });
    this.dialog.addEventListener('click', (event) => { if (event.target.closest('[data-promo-copy]')) this.copy(); }, { signal });
    // Envío: hCaptcha puede pedir un desafío y lo pinta en el documento; un <dialog> modal está en el top layer y lo taparía.
    // Se pasa a no modal (show) en el mismo lugar antes de que Shopify intercepte el submit.
    this.dialog.addEventListener('submit', () => {
      if (!this.dialog.matches(':modal')) return;
      this.dialog.close();
      this.dialog.classList.add('promo--submitting');
      this.dialog.show();
    }, { capture: true, signal });

    this.template = this.dialog.querySelector('template[data-promo-template]');
    const posted = this.template?.content.querySelector('[data-promo-success], .promo__errors');
    const delay = Number(this.dataset.delay) || 0;
    if (posted) {
      this.open();
      const url = new URL(location.href);
      if (url.searchParams.has('customer_posted')) {
        url.searchParams.delete('customer_posted');
        url.hash = '';
        history.replaceState(null, '', url);
      }
    } else if (delay > 0 && !this.dismissed()) {
      // Apertura automática a los N s, solo si la persona ya interactuó con la página (scroll, mouse, teclado, toque): sin
      // interacción (robots, Lighthouse) no se abre y el formulario, con su hCaptcha, no se carga.
      let interacted = false;
      let due = false;
      const tryOpen = () => { if (interacted && due && !this.dialog.open && !document.querySelector('dialog[open]')) this.open(); };
      // scroll solo del documento (sin capture): el de carriles y carruseles internos puede ser programático.
      const events = ['pointerdown', 'pointermove', 'keydown', 'touchstart'];
      const onInteract = () => {
        interacted = true;
        events.forEach((type) => removeEventListener(type, onInteract, { capture: true }));
        removeEventListener('scroll', onInteract);
        tryOpen();
      };
      events.forEach((type) => addEventListener(type, onInteract, { capture: true, passive: true, signal }));
      addEventListener('scroll', onInteract, { passive: true, signal });
      this.timer = setTimeout(() => { due = true; tryOpen(); }, delay * 1000);
    }
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    clearTimeout(this.revealTimer);
    cancelAnimationFrame(this.liftFrame);
    this.observer?.disconnect();
    this.controller?.abort();
    this.controller = null;
  }

  /** T-051: la pestaña «10% off» no aparece al cargar: recién al scrollear un 40 % de la primera pantalla o a los 8 s (lo primero
   * que pase). Una vez visible queda (floats--tab-ready); el CSS la mantiene oculta hasta entonces, también sin JS. */
  revealTab(signal) {
    const ready = () => {
      clearTimeout(this.revealTimer);
      removeEventListener('scroll', onScroll);
      this.classList.add('floats--tab-ready');
    };
    const onScroll = () => { if (scrollY >= innerHeight * 0.4) ready(); };
    addEventListener('scroll', onScroll, { passive: true, signal });
    this.revealTimer = setTimeout(ready, 8000);
    onScroll();
  }

  avoid(signal) {
    // Franjas de precio del panel 3D y su leyenda de «Piezas» (data-float-avoid: suben WhatsApp) y paneles que llegan a la esquina
    // inferior izquierda (data-tab-avoid, T-036: el panel de «Miralas en 3D» a todo el ancho): mientras pasan por ahí, la pestaña
    // «10% off» se oculta (floats--tab-hidden) en vez de taparlos.
    const targets = [...document.querySelectorAll('[data-float-avoid], [data-tab-avoid]')];
    if (!targets.length || !('IntersectionObserver' in window)) return;
    const visible = new Set();
    const update = () => {
      this.liftFrame = 0;
      // De abajo hacia arriba: cada franja que toca el círculo lo empuja, y la siguiente se mide contra su nueva posición
      // (la franja de precio sube el círculo hasta la leyenda de «Piezas», y la leyenda lo sube otra vez).
      const shown = [...visible].filter((el) => !el.hidden && getComputedStyle(el).visibility !== 'hidden');
      const tabZone = innerHeight - TAB_ZONE;
      this.classList.toggle('floats--tab-hidden', shown.some((el) => {
        if (!el.hasAttribute('data-tab-avoid')) return false;
        const r = el.getBoundingClientRect();
        return r.bottom > tabZone && r.top < innerHeight;
      }));
      const rects = shown
        .filter((el) => el.hasAttribute('data-float-avoid'))
        .map((el) => el.getBoundingClientRect())
        .filter((rect) => rect.top < innerHeight && rect.bottom > 0)
        .sort((a, b) => b.bottom - a.bottom);
      let lift = 0;
      for (const rect of rects) {
        const floatTop = innerHeight - Math.max(lift, FLOAT_BASE) - FLOAT_SIZE - FLOAT_GAP;
        if (rect.bottom > floatTop) lift = Math.max(lift, innerHeight - rect.top + FLOAT_GAP);
      }
      const root = document.documentElement;
      if (lift) root.style.setProperty('--floats-bottom', `${Math.round(lift)}px`);
      else root.style.removeProperty('--floats-bottom');
      this.classList.toggle('floats--lift', lift > 0);
      this.classList.toggle('floats--compact', shown.some((el) => el.dataset.floatAvoid === 'compact'));
    };
    const schedule = () => { if (!this.liftFrame) this.liftFrame = requestAnimationFrame(update); };
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) visible.add(entry.target); else visible.delete(entry.target); });
      if (visible.size) {
        if (!this.scrolling) { this.scrolling = true; addEventListener('scroll', schedule, { passive: true, signal }); }
      } else if (this.scrolling) { this.scrolling = false; removeEventListener('scroll', schedule); }
      schedule();
    });
    targets.forEach((el) => this.observer.observe(el));
    addEventListener('resize', schedule, { passive: true, signal });
    // El hero cambia de franja al elegir producto y muestra la leyenda al pasar a «Piezas»: se recalcula al toque.
    document.addEventListener('hero:product', schedule, { signal });
    document.addEventListener('change', (event) => { if (event.target.matches?.('[data-build-option]')) setTimeout(schedule, 50); }, { signal });
  }

  dismissed() {
    try {
      const until = Number(localStorage.getItem(this.dataset.key));
      return Number.isFinite(until) && until > Date.now();
    } catch { return false; }
  }

  remember() {
    try {
      const days = Number(this.dataset.days) || 14;
      localStorage.setItem(this.dataset.key, String(Date.now() + days * 864e5));
    } catch { /* Sin almacenamiento: el popup puede volver a abrirse. */ }
  }

  /** Clona el formulario del <template> la primera vez (antes de abrir el popup). */
  mountForm() {
    if (!this.template || this.formMounted) return;
    this.formMounted = true;
    this.template.after(this.template.content.cloneNode(true));
  }

  open() {
    clearTimeout(this.timer);
    if (this.dialog.open) return;
    this.opener = document.activeElement;
    this.mountForm();
    this.dialog.showModal();
    (this.dialog.querySelector('input[type=email]') || this.dialog.querySelector('[data-promo-copy]') || this.dialog.querySelector('[data-promo-close]'))?.focus();
  }

  close() {
    if (!this.dialog.open) return;
    this.remember();
    this.dialog.close();
    this.dialog.classList.remove('promo--submitting');
    (this.opener?.isConnected ? this.opener : this.tab)?.focus();
  }

  async copy() {
    const button = this.querySelector('[data-promo-copy]');
    const code = this.querySelector('[data-promo-code]')?.textContent.trim();
    const status = this.querySelector('[data-promo-copy-status]');
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const range = document.createRange();
      range.selectNodeContents(this.querySelector('[data-promo-code]'));
      const selection = getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      try { document.execCommand('copy'); } catch { /* el usuario puede copiar a mano */ }
    }
    const label = button.querySelector('span');
    const original = label.textContent;
    label.textContent = button.dataset.copied;
    if (status) status.textContent = button.dataset.copied;
    clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => { label.textContent = original; }, 2000);
  }
}

if (!customElements.get('promo-float')) customElements.define('promo-float', PromoFloat);
